import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Bot, LayoutDashboard, UserCircle, CreditCard, ShieldCheck, LogOut, Radio, Zap, Cpu, Users, Radar, Newspaper, Trophy, Tent, Phone } from "lucide-react";
import { useAuth, api } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { appTexts } from "@/i18n/appTexts";
import { GoLiveModal } from "@/components/dashboard/GoLiveModal";
import { ProfileSection, SecurityLogSection } from "@/components/dashboard/Sections";
import { PricingTable } from "@/components/dashboard/PricingTable";
import { CreditDashboard } from "@/components/dashboard/CreditDashboard";
import { ConnectionsSection } from "@/components/dashboard/Connections";
import { PersonalityLab } from "@/components/dashboard/PersonalityLab";
import { MatchAlerts } from "@/components/dashboard/MatchAlerts";
import { ProximityRadar } from "@/components/dashboard/ProximityRadar";
import { CyberWall } from "@/components/dashboard/CyberWall";
import { AwardsArena } from "@/components/dashboard/AwardsArena";
import { Tribes } from "@/components/dashboard/Tribes";
import { NeuralLink } from "@/components/dashboard/NeuralLink";
import { ActivationCard } from "@/components/dashboard/ActivationCard";
import { MoodRevival } from "@/components/dashboard/MoodRevival";
import { IntelligenceLab } from "@/components/dashboard/IntelligenceLab";
import { InstallPrompt } from "@/components/InstallPrompt";
import { requestNotifPermission, triggerMatchAlert } from "@/lib/matchAlert";

const ROBOT = "/robots/hero_robot.png";

export default function Dashboard() {
  const { user, setUser, logout } = useAuth();
  const { lang, setLang } = useLang();
  const t = appTexts[lang].dash;
  const tAuth = appTexts[lang].auth;
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [modalOpen, setModalOpen] = useState(false);
  const [matchOn, setMatchOn] = useState(false);

  useEffect(() => {
    api.get("/settings/features").then((r) => setMatchOn(!!r.data.matchmaking_enabled)).catch(() => {});
  }, []);

  const isLive = user.avatar_status === "Active_Proximity";

  // Stripe payment return: poll checkout status (mandatory polling pattern)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const tabParam = params.get("tab");
    const payParam = params.get("pay");
    if (tabParam) setTab(tabParam);
    if (payParam === "cancel") {
      toast.info(t.lab.payCanceled);
      window.history.replaceState({}, "", "/dashboard");
      return;
    }
    if (!sessionId) return;
    window.history.replaceState({}, "", "/dashboard");
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) {
        toast.error(t.lab.payFailed);
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if ((data.package_id || "").startsWith("voice_")) {
            toast.success(t.neural.payVoiceSuccess);
          } else if (data.package_id === "tamagochy_30") {
            setUser({ ...user, tier: "plus", plus_active: true, premium_customization: true });
            toast.success("🥚 ¡PAQUETE TAMAGOCHY activo por 30 días! / TAMAGOCHY PACK active for 30 days!");
          } else {
            setUser({ ...user, premium_customization: true });
            toast.success(t.lab.paySuccess);
          }
          return;
        }
        if (data.status === "expired") {
          toast.error(t.lab.payFailed);
          return;
        }
        toast.info(t.lab.payPending);
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    requestNotifPermission();
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications");
        const unread = data.filter((n) => !n.read);
        if (!unread.length) return;
        unread.slice(0, 3).forEach((n) => {
          if (n.type === "match_mutual") triggerMatchAlert(t.mutualAlertTitle, n.message);
          toast.success(n.message);
        });
        await api.post("/notifications/read-all");
      } catch {}
    };
    poll();
    const id = setInterval(poll, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResult = (status) => {
    setUser({ ...user, avatar_status: status });
    if (status === "Active_Proximity") toast.success(t.dispatched);
    else toast.error(t.restricted);
  };

  const [counts, setCounts] = useState({ connections: 0, matches: 0 });

  const goOffline = async () => {
    await api.post("/avatar/go-offline");
    setUser({ ...user, avatar_status: "Offline" });
  };

  useEffect(() => {
    Promise.all([api.get("/connections"), api.get("/matches")])
      .then(([c, m]) => setCounts({ connections: c.data.filter((x) => x.status === "connected").length, matches: m.data.length }))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: "overview", label: t.overview, icon: LayoutDashboard },
    { id: "neural", label: t.neural.tab, icon: Phone },
    { id: "wall", label: t.wall.tab, icon: Newspaper },
    { id: "tribes", label: t.tribes.tab, icon: Tent },
    { id: "awards", label: t.awards.tab, icon: Trophy },
    { id: "proximity", label: t.proximity, icon: Radar },
    { id: "avatar", label: t.myAvatar, icon: Cpu },
    { id: "connections", label: t.connections, icon: Users },
    { id: "profile", label: t.profile, icon: UserCircle },
    { id: "billing", label: t.billing, icon: CreditCard },
    { id: "security", label: t.security, icon: ShieldCheck },
  ].filter(({ id }) => matchOn || (id !== "proximity" && id !== "connections"));

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex overflow-x-hidden" data-testid="dashboard-page">
      <aside className="hidden md:flex flex-col w-60 border-r border-white/5 bg-[#0F0F1A]/50 p-5 fixed inset-y-0">
        <Link to="/" className="flex items-center gap-2 font-heading font-black text-sm mb-10">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#B026FF] to-[#00F5FF] flex items-center justify-center"><Bot size={18} /></span>
          AVATAR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">4 U</span>
        </Link>
        <nav className="space-y-1 flex-1">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => setTab(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-colors ${
                tab === id ? "bg-[#B026FF]/15 text-white border border-[#B026FF]/30" : "text-white/50 hover:text-white hover:bg-white/5"
              }`}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </nav>
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 mb-4 self-start">
          {["en", "es"].map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
              className={`text-[10px] font-bold px-3 py-1 rounded-full uppercase ${lang === l ? "bg-[#B026FF] text-white" : "text-white/50"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-white/50 hover:text-red-400 hover:bg-red-500/5 transition-colors">
          <LogOut size={17} /> {t.logout}
        </button>
      </aside>

      <main className="flex-1 min-w-0 w-full md:ml-60 p-5 sm:p-6 md:p-10 pb-32">
        <div className="md:hidden flex items-center gap-2 overflow-x-auto mb-6 -mx-1 px-1">
          {tabs.map(({ id, label }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap shrink-0 ${tab === id ? "bg-[#B026FF]/20 border border-[#B026FF]/40" : "bg-white/5 text-white/50"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="overview-section">
            <h1 className="font-heading text-2xl md:text-3xl font-black mb-1">{t.welcome}, {user.name.split(" ")[0]}</h1>
            <p className="text-sm text-[#A0A0B0] mb-8">@{user.username}</p>

            <MoodRevival t={t} user={user} setUser={setUser} />
            <ActivationCard t={t} user={user} setUser={setUser} onCreateAvatar={() => setTab("avatar")} />
            <IntelligenceLab lang={lang} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 relative bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-7 overflow-hidden">
                <div className={`absolute -top-16 -right-16 w-56 h-56 blur-[90px] opacity-25 rounded-full ${isLive ? "bg-[#00FFAA]" : "bg-[#B026FF]"}`} />
                <div className="flex items-center gap-6">
                  <img src={user.avatar_growth?.img || ROBOT} alt="Avatar" className="w-24 h-24 object-contain drop-shadow-[0_0_20px_rgba(0,245,255,0.3)]" data-testid="overview-growth-img" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-1">{t.avatarStatus}</p>
                    <h2 className="font-heading text-xl font-black">{user.avatar_name || "—"}</h2>
                    <span data-testid="avatar-status-badge" className={`inline-flex items-center gap-2 mt-2 text-[10px] font-bold tracking-widest px-3 py-1.5 rounded-full ${
                      isLive ? "bg-[#00FFAA]/10 text-[#00FFAA] border border-[#00FFAA]/40" : "bg-white/5 text-white/40 border border-white/10"
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${isLive ? "bg-[#00FFAA] animate-pulse" : "bg-white/30"}`} />
                      {isLive ? t.statusActive : t.statusOffline}
                    </span>
                  </div>
                </div>
                {isLive && (
                  <button data-testid="go-offline-btn" onClick={goOffline}
                    className="mt-6 px-5 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/5 border border-white/15 hover:bg-white/10">
                    {t.goOffline}
                  </button>
                )}
              </div>

              <div className="space-y-5">
                <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2">{t.plan}</p>
                  <p className="font-heading text-2xl font-black uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">{user.tier || "free"}</p>
                </div>
                {matchOn && (
                <div className="grid grid-cols-2 gap-5">
                  <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-5">
                    <p className="font-heading text-2xl font-black text-[#00F5FF]" data-testid="connections-count">{counts.connections}</p>
                    <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mt-1">{t.connections}</p>
                  </div>
                  <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-5">
                    <p className="font-heading text-2xl font-black text-[#B026FF]" data-testid="matches-count">{counts.matches}</p>
                    <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mt-1">{t.matches}</p>
                  </div>
                </div>
                )}
              </div>
            </div>

            {matchOn && <MatchAlerts t={t} lang={lang} />}
            <CreditDashboard lang={lang} matchOn={matchOn} />
          </div>
        )}

        {tab !== "overview" && (
          <h1 className="font-heading text-2xl font-black mb-6">
            {tab === "neural" ? t.neural.title : tab === "wall" ? t.wall.title : tab === "tribes" ? t.tribes.title : tab === "awards" ? t.awards.title : tab === "proximity" ? t.proximity : tab === "avatar" ? t.lab.title : tab === "connections" ? t.connTitle : tab === "profile" ? t.profile : tab === "billing" ? t.billingTitle : t.securityTitle}
          </h1>
        )}
        {tab === "neural" && <NeuralLink t={t} lang={lang} user={user} setUser={setUser} />}
        {tab === "wall" && <CyberWall t={t} lang={lang} user={user} setUser={setUser} />}
        {tab === "tribes" && <Tribes t={t} lang={lang} />}
        {tab === "awards" && <AwardsArena t={t} lang={lang} />}
        {tab === "proximity" && <ProximityRadar t={t} isLive={isLive} user={user} setUser={setUser} />}
        {tab === "avatar" && (user.service_active
          ? <PersonalityLab t={t} user={user} setUser={setUser} />
          : <ActivationCard t={t} user={user} setUser={setUser} onCreateAvatar={() => setTab("avatar")} />)}
        {tab === "connections" && <ConnectionsSection t={t} />}
        {tab === "profile" && <ProfileSection t={t} tAuth={tAuth} user={user} setUser={setUser} />}
        {tab === "billing" && <PricingTable lang={lang} user={user} setUser={setUser} />}
        {tab === "security" && <SecurityLogSection t={t} />}
      </main>

      {matchOn && !isLive && (
        <button data-testid="go-live-fab" onClick={() => setModalOpen(true)}
          className="fixed bottom-8 right-8 z-50 flex items-center gap-3 px-7 py-4 rounded-full font-heading font-black text-sm uppercase tracking-wider bg-gradient-to-r from-[#B026FF] to-[#7B1FD6] border border-[#D080FF]/40 shadow-[0_0_30px_rgba(176,38,255,0.6)] hover:shadow-[0_0_45px_rgba(176,38,255,0.85)] hover:-translate-y-1 transition-all animate-neon-pulse">
          <Radio size={18} />
          {t.goLive}
          <Zap size={16} className="fill-white" />
        </button>
      )}

      <GoLiveModal t={t} open={modalOpen} onClose={() => setModalOpen(false)} onResult={handleResult} />
      <InstallPrompt lang={lang} />
    </div>
  );
}
